import { useState, useEffect } from "react";
import { api } from "@/App";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  Wrench,
  RefreshCw,
  Search,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  Shield,
  Zap,
  Database,
  FileSignature,
  Download,} from "lucide-react";

const StatusBadge = ({ status }) => {
  const map = {
    signed: { color: "bg-emerald-100 text-emerald-800", label: "Signé" },
    pending_seller: { color: "bg-amber-100 text-amber-800", label: "Attente vendeur" },
    pending_manager: { color: "bg-blue-100 text-blue-800", label: "Attente manager" },
    draft: { color: "bg-gray-100 text-gray-800", label: "Brouillon" },
    archived: { color: "bg-red-100 text-red-800", label: "Archivé" },
    done: { color: "bg-emerald-100 text-emerald-800", label: "Terminé" },
    ongoing: { color: "bg-amber-100 text-amber-800", label: "En cours" },
    refused: { color: "bg-red-100 text-red-800", label: "Refusé" },
    expired: { color: "bg-gray-100 text-gray-800", label: "Expiré" },
  };
  const info = map[status] || { color: "bg-gray-100 text-gray-600", label: status || "?" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${info.color}`}>
      {info.label}
    </span>
  );
};

const ToolCard = ({ icon: Icon, title, description, buttonLabel, onClick, loading, result, variant = "default" }) => {
  const [expanded, setExpanded] = useState(false);

  const variantStyles = {
    default: "border-gray-200 hover:border-gray-300",
    warning: "border-amber-200 hover:border-amber-300 bg-amber-50/30",
    danger: "border-red-200 hover:border-red-300 bg-red-50/30",
  };

  const buttonStyles = {
    default: "bg-[#212E38] hover:bg-[#2a3a47] text-white",
    warning: "bg-amber-600 hover:bg-amber-700 text-white",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };

  return (
    <div className={`border rounded-lg p-5 transition-all ${variantStyles[variant]}`} data-testid={`tool-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="p-2 bg-gray-100 rounded-lg">
          <Icon className="w-5 h-5 text-gray-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-sm">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>

      <button
        onClick={onClick}
        disabled={loading}
        className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${buttonStyles[variant]}`}
        data-testid={`tool-btn-${title.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            En cours...
          </span>
        ) : (
          buttonLabel
        )}
      </button>

      {result && (
        <div className="mt-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            data-testid="tool-result-toggle"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Voir les détails
          </button>
          {expanded && (
            <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-auto max-h-96 border border-gray-200">
              {JSON.stringify(result, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default function AdminToolsPage() {
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagResult, setDiagResult] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [forceFixLoading, setForceFixLoading] = useState(false);
  const [forceFixResult, setForceFixResult] = useState(null);
  const [fleetDiagLoading, setFleetDiagLoading] = useState(false);
  const [fleetDiagResult, setFleetDiagResult] = useState(null);
  const [fleetFixLoading, setFleetFixLoading] = useState(false);
  const [fleetFixResult, setFleetFixResult] = useState(null);
  const [forceSyncLoading, setForceSyncLoading] = useState(false);
  const [forceSyncResult, setForceSyncResult] = useState(null);
  const [mgrDiagLoading, setMgrDiagLoading] = useState(false);
  const [mgrDiagResult, setMgrDiagResult] = useState(null);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [managers, setManagers] = useState([]);
  const [concessions, setConcessions] = useState([]);
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedConcessions, setSelectedConcessions] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  // Load managers and concessions for assignment tool
  useEffect(() => {
    api.get("/managers").then(res => setManagers(res.data || [])).catch(() => {});
    api.get("/concessions").then(res => setConcessions(res.data || [])).catch(() => {});
  }, []);

  const runDiagnoseYousign = async () => {
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const res = await api.get("/admin/diagnose-yousign");
      setDiagResult(res.data);
      if (res.data.issues_found > 0) {
        toast.warning(`${res.data.issues_found} problème(s) détecté(s) dont ${res.data.auto_fixable} corrigeable(s)`);
      } else {
        toast.success("Aucun problème détecté");
      }
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setDiagLoading(false);
    }
  };

  const runSyncYousign = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await api.post("/admin/sync-yousign-statuses");
      setSyncResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setSyncLoading(false);
    }
  };

  const runForceFixYousign = async () => {
    setForceFixLoading(true);
    setForceFixResult(null);
    try {
      const res = await api.post("/admin/force-fix-yousign");
      setForceFixResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setForceFixLoading(false);
    }
  };

  const runDiagnoseFleet = async () => {
    setFleetDiagLoading(true);
    setFleetDiagResult(null);
    try {
      const res = await api.get("/admin/diagnose-fleet");
      setFleetDiagResult(res.data);
      if (res.data.issues?.length > 0) {
        toast.warning(`${res.data.issues.length} problème(s) Fleet détecté(s)`);
      } else {
        toast.success("Configuration Fleet OK");
      }
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setFleetDiagLoading(false);
    }
  };

  const runFixFleet = async () => {
    setFleetFixLoading(true);
    setFleetFixResult(null);
    try {
      const res = await api.post("/admin/fix-fleet");
      setFleetFixResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setFleetFixLoading(false);
    }
  };

  const runForceSync = async () => {
    setForceSyncLoading(true);
    setForceSyncResult(null);
    try {
      const res = await api.post("/admin/force-sync-signatures");
      setForceSyncResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setForceSyncLoading(false);
    }
  };

  const runDiagnoseManagers = async () => {
    setMgrDiagLoading(true);
    setMgrDiagResult(null);
    try {
      const res = await api.get("/admin/diagnose-managers");
      setMgrDiagResult(res.data);
      if (res.data.issues_count > 0) {
        toast.warning(`${res.data.issues_count} problème(s) détecté(s)`);
      } else {
        toast.success("Tous les managers sont correctement configurés");
      }
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setMgrDiagLoading(false);
    }
  };

  const runResetPassword = async () => {
    setResetLoading(true);
    setResetResult(null);
    try {
      const res = await api.post("/admin/reset-user-password", {
        email: resetEmail,
        new_password: resetPassword
      });
      setResetResult(res.data);
      toast.success(res.data.message);
      setResetEmail("");
      setResetPassword("");
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setResetLoading(false);
    }
  };

  const [backupLoading, setBackupLoading] = useState(false);
  const runBackupDownload = async () => {
    setBackupLoading(true);
    try {
      const res = await api.get("/admin/backup/download", { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename=([^;]+)/i);
      const filename = match ? match[1].trim() : `verbaere_backup_${Date.now()}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup téléchargé");
    } catch (err) {
      toast.error("Erreur backup: " + (err.response?.data?.detail || err.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleSelectManager = (email) => {
    setSelectedManager(email);
    setAssignResult(null);
    const mgr = managers.find(m => m.email === email);
    setSelectedConcessions(mgr?.concession_ids || (mgr?.concession_id ? [mgr.concession_id] : []));
  };

  const toggleConcession = (concId) => {
    setSelectedConcessions(prev => 
      prev.includes(concId) ? prev.filter(id => id !== concId) : [...prev, concId]
    );
  };

  const runAssignConcessions = async () => {
    if (!selectedManager) return;
    setAssignLoading(true);
    setAssignResult(null);
    try {
      const res = await api.post("/admin/update-manager-concessions", {
        email: selectedManager,
        concession_ids: selectedConcessions
      });
      setAssignResult(res.data);
      toast.success(res.data.message);
      // Refresh managers
      api.get("/managers").then(r => setManagers(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error("Erreur: " + (err.response?.data?.detail || err.message));
    } finally {
      setAssignLoading(false);
    }
  };

  // Summary cards for diagnostic results
  const renderDiagSummary = () => {
    if (!diagResult || diagResult.error) return null;
    
    const issues = diagResult.diagnostics?.filter(d => d.issues?.length > 0) || [];
    
    return (
      <div className="mt-4 space-y-3" data-testid="diag-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{diagResult.total_with_yousign}</div>
            <div className="text-xs text-gray-500">Total objectifs</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-900">{diagResult.checked}</div>
            <div className="text-xs text-gray-500">Vérifiés</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${diagResult.issues_found > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
            <div className={`text-2xl font-bold ${diagResult.issues_found > 0 ? "text-red-700" : "text-emerald-700"}`}>
              {diagResult.issues_found}
            </div>
            <div className="text-xs text-gray-500">Problèmes</div>
          </div>
          <div className={`rounded-lg p-3 text-center ${diagResult.auto_fixable > 0 ? "bg-amber-50" : "bg-gray-50"}`}>
            <div className={`text-2xl font-bold ${diagResult.auto_fixable > 0 ? "text-amber-700" : "text-gray-400"}`}>
              {diagResult.auto_fixable}
            </div>
            <div className="text-xs text-gray-500">Corrigeables</div>
          </div>
        </div>

        {issues.length > 0 && (
          <div className="border border-red-200 rounded-lg overflow-hidden">
            <div className="bg-red-50 px-4 py-2 border-b border-red-200">
              <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Objectifs avec problèmes
              </h4>
            </div>
            <div className="divide-y divide-red-100">
              {issues.map((d, i) => (
                <div key={i} className="px-4 py-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">
                      {d.seller_name} - {d.concession} ({d.month}/{d.year})
                    </span>
                    <div className="flex gap-2">
                      <StatusBadge status={d.local_status} />
                      <span className="text-gray-400">vs</span>
                      <StatusBadge status={d.yousign_api_status} />
                    </div>
                  </div>
                  <ul className="text-xs text-red-600 space-y-0.5">
                    {d.issues.map((issue, j) => (
                      <li key={j} className="flex items-start gap-1">
                        <XCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        {issue}
                      </li>
                    ))}
                  </ul>
                  {d.yousign_raw_signers?.length > 0 && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Signataires Yousign:</span>
                      {d.yousign_raw_signers.map((s, k) => (
                        <span key={k} className="ml-2">
                          {s.email} ({s.status})
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {issues.length === 0 && (
          <div className="flex items-center gap-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <span className="text-sm text-emerald-800 font-medium">
              Tous les objectifs Yousign sont correctement synchronisés
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="p-6 max-w-5xl mx-auto" data-testid="admin-tools-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-[#212E38] rounded-xl">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Outils Administration</h1>
              <p className="text-sm text-gray-500">Diagnostic et maintenance du système</p>
            </div>
          </div>
        </div>

        {/* Yousign Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileSignature className="w-5 h-5 text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">Signatures Yousign</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ToolCard
              icon={Search}
              title="Diagnostic Yousign"
              description="Analyse complète : compare les données locales avec l'API Yousign et identifie les écarts"
              buttonLabel="Lancer le diagnostic"
              onClick={runDiagnoseYousign}
              loading={diagLoading}
              result={diagResult}
            />
            <ToolCard
              icon={RefreshCw}
              title="Synchroniser les statuts"
              description="Met à jour les objectifs en attente avec les statuts réels de Yousign"
              buttonLabel="Synchroniser"
              onClick={runSyncYousign}
              loading={syncLoading}
              result={syncResult}
            />
            <ToolCard
              icon={Zap}
              title="Forcer la correction"
              description="Corrige les signatures en utilisant le matching par email (si les IDs ne correspondent pas)"
              buttonLabel="Forcer la correction"
              onClick={runForceFixYousign}
              loading={forceFixLoading}
              result={forceFixResult}
              variant="warning"
            />
            <ToolCard
              icon={Shield}
              title="Force Sync Complet"
              description="Vérifie et corrige TOUS les objectifs non signés (pas seulement en attente)"
              buttonLabel="Force sync"
              onClick={runForceSync}
              loading={forceSyncLoading}
              result={forceSyncResult}
              variant="warning"
            />
          </div>

          {renderDiagSummary()}
        </section>

        {/* Fleet Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">Configuration Fleet</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ToolCard
              icon={Search}
              title="Diagnostic Fleet"
              description="Vérifie la configuration de la concession Fleet, THOOR et les vendeurs associés"
              buttonLabel="Diagnostiquer Fleet"
              onClick={runDiagnoseFleet}
              loading={fleetDiagLoading}
              result={fleetDiagResult}
            />
            <ToolCard
              icon={Wrench}
              title="Corriger Fleet"
              description="Crée/corrige la concession Fleet et associe THOOR automatiquement"
              buttonLabel="Appliquer les corrections"
              onClick={runFixFleet}
              loading={fleetFixLoading}
              result={fleetFixResult}
              variant="danger"
            />
          </div>
        </section>

        {/* Managers Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">Gestion des accès</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <ToolCard
              icon={Search}
              title="Diagnostic Managers"
              description="Vérifie les accès des managers : mot de passe, concessions, configuration"
              buttonLabel="Diagnostiquer"
              onClick={runDiagnoseManagers}
              loading={mgrDiagLoading}
              result={mgrDiagResult}
            />
            <div className="border rounded-lg p-5" data-testid="tool-card-reset-password">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Zap className="w-5 h-5 text-gray-700" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm">Réinitialiser un mot de passe</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Définir un nouveau mot de passe pour un utilisateur</p>
                </div>
              </div>
              <div className="space-y-2 mb-3">
                <input
                  type="email"
                  placeholder="Email de l'utilisateur"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  data-testid="reset-email-input"
                />
                <input
                  type="text"
                  placeholder="Nouveau mot de passe (min 8 car.)"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                  data-testid="reset-password-input"
                />
              </div>
              <button
                onClick={runResetPassword}
                disabled={resetLoading || !resetEmail || !resetPassword}
                className="w-full py-2 px-4 rounded-md text-sm font-medium bg-[#212E38] hover:bg-[#2a3a47] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-password-btn"
              >
                {resetLoading ? "En cours..." : "Réinitialiser"}
              </button>
              {resetResult && (
                <p className="mt-2 text-xs text-emerald-600 font-medium">{resetResult.message}</p>
              )}
            </div>
          </div>

          {/* Assign concessions to manager */}
          <div className="mt-4 border rounded-lg p-5" data-testid="tool-card-assign-concessions">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Database className="w-5 h-5 text-gray-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-sm">Assigner des concessions à un manager</h3>
                <p className="text-xs text-gray-500 mt-0.5">Cochez les concessions auxquelles le manager doit avoir accès</p>
              </div>
            </div>
            <div className="space-y-3">
              <select
                value={selectedManager}
                onChange={(e) => handleSelectManager(e.target.value)}
                className="w-full h-9 rounded-md border border-gray-300 bg-white px-3 text-sm focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                data-testid="assign-manager-select"
              >
                <option value="">Sélectionner un manager...</option>
                {managers.map(m => (
                  <option key={m.email} value={m.email}>{m.name} ({m.email})</option>
                ))}
              </select>

              {selectedManager && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border rounded-lg bg-gray-50 max-h-56 overflow-y-auto">
                    {concessions.map(c => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1.5 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedConcessions.includes(c.id)}
                          onChange={() => toggleConcession(c.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          data-testid={`assign-conc-${c.id}`}
                        />
                        <span className={`text-sm ${selectedConcessions.includes(c.id) ? 'font-medium text-blue-800' : 'text-gray-700'}`}>
                          {c.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{selectedConcessions.length} concession(s) sélectionnée(s)</span>
                    <button
                      onClick={runAssignConcessions}
                      disabled={assignLoading || selectedConcessions.length === 0}
                      className="py-2 px-4 rounded-md text-sm font-medium bg-[#212E38] hover:bg-[#2a3a47] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="assign-save-btn"
                    >
                      {assignLoading ? "En cours..." : "Enregistrer"}
                    </button>
                  </div>
                  {assignResult && (
                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <p className="text-sm text-emerald-700 font-medium">{assignResult.message}</p>
                      <p className="text-xs text-emerald-600 mt-1">{assignResult.concessions?.join(", ")}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        {/* Backups Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-gray-700" />
            <h2 className="text-base font-semibold text-gray-900">Backups</h2>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200" data-testid="backup-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-emerald-50 rounded-lg">
                <Download className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">Télécharger un backup complet</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Génère et télécharge un dump JSON de toutes les collections (users, concessions, sellers, objectives, seasonality, brands). Les hachages de mot de passe sont expurgés pour raisons de sécurité.
                </p>
              </div>
            </div>
            <button
              onClick={runBackupDownload}
              disabled={backupLoading}
              className="py-2 px-4 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors disabled:opacity-50"
              data-testid="backup-download-btn"
            >
              {backupLoading ? "Génération..." : "Télécharger le backup JSON"}
            </button>
          </div>
        </section>
      </div>
    </Layout>
  );
}
